import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { trackEvent } from '../utils/analytics';
import { 
  MessageCircle, 
  UserPlus, 
  User, 
  GraduationCap,
  Eye, 
  EyeOff, 
  Bot, 
  Users, 
  Sparkles, 
  Heart, 
  Shield, 
  Loader
} from 'lucide-react';
import EmailVerificationForm from './EmailVerificationForm';
import AccountSetupProcess from './AccountSetupProcess';
import { GoogleLogin } from '@react-oauth/google';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function ExpressSignup({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Signup mode detection
  const [signupMode, setSignupMode] = useState('express'); // 'express', 'invitation', 'standard', 'therapist'
  const [invitationData, setInvitationData] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    role: 'patient',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: 'Anonym', // Pre-filled with 'Anonym' for express signup
    last_name: '',
    phone: '',
    date_of_birth: ''
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [showManualLogin, setShowManualLogin] = useState(false);
  
  // Account setup process state
  const [showAccountSetup, setShowAccountSetup] = useState(false);
  const [registrationPassword, setRegistrationPassword] = useState('');
  const [authToken, setAuthToken] = useState(''); // Store auth token for AccountSetup

  // Anonymous message tracking
  const [savedMessages, setSavedMessages] = useState([]);
  const [localAnonymousMessageCount, setLocalAnonymousMessageCount] = useState(0);

  // Debug effect to track showAccountSetup changes
  useEffect(() => {
    console.log('🔍 STATE CHANGE - showAccountSetup is now:', showAccountSetup);
    console.log('🔍 STATE CHANGE - showEmailVerification is now:', showEmailVerification);
    console.log('🔍 STATE CHANGE - authToken is now:', authToken ? `${authToken.substring(0, 20)}...` : 'null');
    console.log('🔍 STATE CHANGE - registeredEmail is now:', registeredEmail);
  }, [showAccountSetup, showEmailVerification, authToken, registeredEmail]);

  // Detect signup mode and load data
  useEffect(() => {
    const pathname = location.pathname;
    const state = location.state;

    // Track express signup start if coming from life themes
    if (state?.from === 'life-themes') {
      trackEvent('Express Signup Start', {
        source: 'life-themes',
        location: 'express-signup'
      });
    }

    // Determine signup mode based on route and state
    if (pathname.includes('/invitation-register') || state?.invitationCode) {
      setSignupMode('invitation');
      setFormData(prev => ({ ...prev, role: 'patient' }));
      
      // Pre-fill invitation data
      if (state?.invitationCode) {
        setInvitationData(state);
        if (state.email) {
          setFormData(prev => ({ ...prev, email: state.email }));
        }
      }
    } else if (pathname.includes('/register')) {
      setSignupMode('standard');
    } else {
      setSignupMode('express');
      setFormData(prev => ({ ...prev, role: 'patient' }));
    }

    // Load saved messages for express signup
    if (signupMode === 'express') {
      const sessionMessages = JSON.parse(sessionStorage.getItem('instantChatMessages') || '[]');
      console.log('🔍 LOADING EXPRESS MESSAGES:', {
        from: 'instantChatMessages',
        count: sessionMessages.length,
        preview: sessionMessages.slice(0, 2).map(msg => ({
          type: msg.type || msg.role,
          content: msg.content?.substring(0, 50) + '...'
        }))
      });
      setSavedMessages(sessionMessages);

      // Count localStorage messages separately
      const localMessages = JSON.parse(localStorage.getItem('anonymousMessages') || '[]');
      const userMsgCount = localMessages.filter(msg => msg.type === 'user').length;
      setLocalAnonymousMessageCount(userMsgCount);
      console.log('🔍 LOCAL MESSAGES COUNT:', userMsgCount);
    }
  }, [location, signupMode]);

  const handleGoogleSignup = async (credentialResponse) => {
    setIsLoading(true);
    setError('');

    try {
      console.log('🔄 Attempting Google signup/login...');

      const response = await axios.post(`${API_BASE}/auth/google/login`, {
        credential: credentialResponse.credential
      });

      const token = response.data.access_token;
      const userData = response.data.user;
      const isNewUser = response.data.is_new_user;

      console.log('✅ Google signup/login successful for:', userData.email);
      console.log('🆕 Is new user:', isNewUser);
      console.log('🔍 DEBUG: Full response:', response.data);

      // Track Google signup/login
      trackEvent(isNewUser ? 'User Registered' : 'User Logged In', {
        method: 'google',
        role: userData.role,
        signup_mode: signupMode
      });

      // Store token
      localStorage.setItem('therapy_auth_token', token);
      
      // If new user from express signup, show account setup
      if (isNewUser && signupMode === 'express') {
        console.log('🎯 New Google user - showing AccountSetup');
        console.log('🔍 DEBUG: Setting state - showAccountSetup = true');
        setRegisteredEmail(userData.email);
        setAuthToken(token);
        
        // Convert any saved messages
        if (savedMessages.length > 0) {
          sessionStorage.setItem('pendingAnamnesisMessages', JSON.stringify(savedMessages));
          console.log('💾 Saved messages for AccountSetup:', savedMessages.length);
        }
        
        setShowAccountSetup(true);
        setIsLoading(false);
        console.log('🎯 Returning early - should NOT call onLogin or navigate');
        return;
      }
      
      console.log('🔍 DEBUG: Not a new user or not express signup, proceeding to login');
      
      // Existing user or non-express signup - convert messages and proceed
      if (signupMode === 'express' && savedMessages.length > 0) {
        try {
          console.log(`🔄 Converting ${savedMessages.length} anonymous messages...`);
          
          const convertResponse = await axios.post(
            `${API_BASE}/auth/convert-anonymous-messages`,
            { messages: savedMessages },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          if (convertResponse.data.success) {
            console.log(`✅ Converted ${convertResponse.data.converted_count} messages`);
            sessionStorage.removeItem('instantChatMessages');
          }
        } catch (conversionError) {
          console.error('⚠️ Message conversion failed:', conversionError);
        }
      }

      // Call onLogin and navigate
      if (onLogin) {
        onLogin(token, userData);
      }
      
      navigate('/client');
    } catch (err) {
      console.error('❌ Google signup/login error:', err);
      setError(err.response?.data?.detail || 'Google-registrering misslyckades');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const validateForm = () => {
    const { email, password, confirmPassword, first_name, last_name, role } = formData;
    
    // Common validation
    if (!email || !password || !confirmPassword) {
      setError('Alla fält är obligatoriska');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Ange en giltig e-postadress');
      return false;
    }

    if (password.length < 8) {
      setError('Lösenordet måste vara minst 8 tecken');
      return false;
    }

    if (!/[A-Z]/.test(password)) {
      setError('Lösenordet måste innehålla minst en versal bokstav (A-Z)');
      return false;
    }

    if (!/[0-9]/.test(password)) {
      setError('Lösenordet måste innehålla minst en siffra (0-9)');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Lösenorden matchar inte');
      return false;
    }

    // Additional validation for standard/invitation signup
    if (signupMode === 'standard' || signupMode === 'invitation') {
      if (!first_name || !last_name) {
        setError('Förnamn och efternamn är obligatoriska');
        return false;
      }
    }

    return true;
  };

  const getRegistrationPayload = () => {
    const basePayload = {
      role: formData.role,
      email: formData.email,
      password: formData.password
    };

    if (signupMode === 'express') {
      // Express signup - minimal data + anonymous messages, using friendly name from form
      return {
        ...basePayload,
        first_name: formData.first_name, // Use the "Vad ska vi kalla dig?" value
        anonymous_messages: savedMessages.length > 0 ? savedMessages : null
      };
    } else if (signupMode === 'invitation') {
      // Invitation signup - register with invitation
      return {
        ...basePayload,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        date_of_birth: formData.date_of_birth,
        invitation_code: invitationData?.invitationCode
      };
    } else {
      // Standard signup - full form
      const payload = {
        ...basePayload,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        date_of_birth: formData.date_of_birth
      };

      return payload;
    }
  };

  const getRegistrationEndpoint = () => {
    if (signupMode === 'invitation') {
      return `${API_BASE}/api/invitations/register-with-invitation`;
    }
    // Use express-register for express signup to auto-verify email
    if (signupMode === 'express') {
      return `${API_BASE}/auth/express-register`;
    }
    // Use regular register endpoint for standard signup with email verification
    return `${API_BASE}/auth/register`;
  };

  const getAnonymousMessages = () => {
    return JSON.parse(localStorage.getItem('anonymousMessages') || '[]');
  };

  const handleAutoLogin = async (registrationData) => {
    try {
      console.log('🔄 Attempting auto-login after express registration...');
      console.log('📧 Login credentials:', { email: formData.email, hasPassword: !!formData.password });
      
      const loginPayload = new URLSearchParams({
        username: formData.email,
        password: formData.password
      });
      
      console.log('🚀 Making login request to:', `${API_BASE}/auth/token`);
      
      const loginResponse = await axios.post(`${API_BASE}/auth/token`, loginPayload, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (loginResponse.data.access_token) {
        console.log('✅ Auto-login successful');
        
        // Store token
        localStorage.setItem('therapy_auth_token', loginResponse.data.access_token);
        
        // NOTE: Message conversion is now handled by AccountSetupProcess
        // Skip conversion here since we're going through email verification → AccountSetup flow
        console.log('ℹ️ Skipping message conversion in ExpressSignup - will be handled by AccountSetup');
        
        // Note: Anamnesis profile should already be created at registration time for express signup

        // Call onLogin callback and navigate
        if (onLogin) {
          onLogin(loginResponse.data.access_token);
        }
        
        console.log('🚀 Navigating to client dashboard...');
        navigate('/client');
      } else {
        console.error('❌ No access token in login response:', loginResponse.data);
        throw new Error('No access token received');
      }
    } catch (error) {
      console.error('❌ Auto-login failed:', error);
      console.error('📋 Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Provide more specific error messages based on status code
      if (error.response?.status === 403) {
        setError('Registrering lyckades men kontot kanske behöver verifieras. Kontrollera din e-post eller försök logga in manuellt.');
      } else if (error.response?.status === 401) {
        setError('Registrering lyckades men inloggningsuppgifterna fungerade inte. Försök logga in manuellt.');
      } else if (error.response?.status === 422) {
        setError('Registrering lyckades men det var ett problem med inloggningsformatet. Försök logga in manuellt.');
      } else {
        setError('Registrering lyckades men automatisk inloggning misslyckades. Försök logga in manuellt.');
      }
      
      // Re-throw the error so it can be caught in handleSubmit
      throw error;
    }
  };

  const handleManualLogin = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      console.log('🔄 Attempting manual login after registration...');
      
      const loginPayload = new URLSearchParams({
        username: registeredEmail,
        password: formData.password
      });
      
      const loginResponse = await axios.post(`${API_BASE}/auth/token`, loginPayload, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      if (loginResponse.data.access_token) {
        console.log('✅ Manual login successful');
        
        // Store token
        localStorage.setItem('therapy_auth_token', loginResponse.data.access_token);
        
        // Check if user is verified - if not, show email verification
        try {
          const userResponse = await axios.get(`${API_BASE}/auth/me`, {
            headers: { 'Authorization': `Bearer ${loginResponse.data.access_token}` }
          });
          
          if (!userResponse.data.is_verified) {
            console.log('User not verified after manual login, showing email verification form');
            // Send verification email and show verification form
            try {
              await axios.post(`${API_BASE}/auth/send-verification-email`, {}, {
                headers: { 'Authorization': `Bearer ${loginResponse.data.access_token}` }
              });
              console.log('Verification email sent');
            } catch (emailError) {
              console.error('Error sending verification email:', emailError);
            }
            
            setShowManualLogin(false);
            setShowEmailVerification(true);
            return; // Don't proceed to dashboard
          }
        } catch (userCheckError) {
          console.error('Error checking user verification status:', userCheckError);
          // If we can't check, assume not verified and show verification
          setShowManualLogin(false);
          setShowEmailVerification(true);
          return;
        }
        
        // User is verified - proceed with full setup
        // Convert anonymous messages if any exist (for express signup) and not already processed
        if (signupMode === 'express' && savedMessages.length > 0 && !sessionStorage.getItem('messagesAlreadyConverted')) {
          try {
            console.log(`🔄 Converting ${savedMessages.length} anonymous messages...`);
            
            const convertResponse = await axios.post(`${API_BASE}/auth/convert-anonymous-messages`, 
              { messages: savedMessages },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${loginResponse.data.access_token}`
                }
              }
            );
            
            if (convertResponse.data.converted_count > 0) {
              console.log(`✅ Successfully converted ${convertResponse.data.converted_count} messages`);
              sessionStorage.removeItem('instantChatMessages');
              sessionStorage.setItem('messagesAlreadyConverted', 'true');
            }
          } catch (conversionError) {
            console.error('⚠️ Error converting messages:', conversionError);
          }
        }

        // Initialize anamnesis profile (only if it doesn't exist yet)
        try {
          console.log('🌱 Checking if anamnesis profile exists...');
          
          // Check if profile already exists first
          try {
            await axios.get(`${API_BASE}/api/progressive-anamnesis/progress`, {
              headers: { 'Authorization': `Bearer ${loginResponse.data.access_token}` }
            });
            console.log('✅ Anamnesis profile already exists, skipping creation');
          } catch (checkError) {
            if (checkError.response?.status === 404) {
              console.log('📝 No anamnesis profile found, creating one...');
              // Create initialization payload with anonymous messages for analysis
              const initPayload = {};
              if (signupMode === 'express' && savedMessages.length > 0) {
                // Include the anonymous messages for anamnesis analysis
                initPayload.anonymous_messages = savedMessages;
                console.log(`📋 Including ${savedMessages.length} anonymous messages for anamnesis analysis`);
              }
              
              await axios.post(`${API_BASE}/api/progressive-anamnesis/initialize-profile`, initPayload, {
                headers: { 'Authorization': `Bearer ${loginResponse.data.access_token}` }
              });
              console.log('✅ Anamnesis profile created with anonymous message analysis');
            } else {
              console.error('⚠️ Error checking anamnesis profile:', checkError);
            }
          }
        } catch (anamnesisError) {
          console.error('⚠️ Error with anamnesis profile:', anamnesisError);
        }
        
        // Call onLogin callback and navigate
        if (onLogin) {
          onLogin(loginResponse.data.access_token);
        }
        
        navigate('/secure-chat');
      }
    } catch (error) {
      console.error('❌ Manual login failed:', error);
      console.error('📋 Manual login error details:', {
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Provide specific error messages
      if (error.response?.status === 403) {
        setError('Kontot behöver verifieras. Kontrollera din e-post för verifieringslänk.');
      } else if (error.response?.status === 401) {
        setError('Fel e-post eller lösenord. Kontrollera dina uppgifter.');
      } else {
        setError('Inloggning misslyckades. Kontrollera din e-post för verifiering eller försök senare.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoading) return;

    if (!validateForm()) return;

    // Track create account button click
    trackEvent('Create Account Button Click', {
      location: 'express-signup',
      signup_mode: signupMode,
      has_anonymous_messages: savedMessages.length > 0,
      message_count: savedMessages.length
    });

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Store anonymous messages for express signup
      if (signupMode === 'express' && savedMessages.length > 0) {
        console.log('Sparar anonyma meddelanden:', savedMessages);
        sessionStorage.setItem('anonymousMessages', JSON.stringify(savedMessages));
      }

      const payload = getRegistrationPayload();
      const endpoint = getRegistrationEndpoint();

      console.log('Registrering:', { endpoint, signupMode, payload: { ...payload, password: '[HIDDEN]' } });

      const response = await axios.post(endpoint, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      const data = response.data;
      console.log('Registrering framgångsrik:', data);

      // Store user data and handle authentication
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      // Store access token if provided (for invitation and other flows)
      if (data.access_token) {
        console.log('🔑 Storing access token from registration response');
        localStorage.setItem('therapy_auth_token', data.access_token);
      }

      // Handle different success scenarios
      if (signupMode === 'express') {
        // Express signup - show email verification first, then account setup
        setRegisteredEmail(formData.email);
        setRegistrationPassword(formData.password);
        
        // Store anonymous messages temporarily for verification process
        if (savedMessages.length > 0) {
          sessionStorage.setItem('pendingAnamnesisMessages', JSON.stringify(savedMessages));
          console.log(`📦 STORING PENDING MESSAGES:`, {
            count: savedMessages.length,
            storageKey: 'pendingAnamnesisMessages',
            preview: savedMessages.slice(0, 2).map(msg => ({ 
              type: msg.type || msg.role, 
              content: msg.content?.substring(0, 50) + '...' 
            }))
          });
        } else {
          console.log('⚠️ NO MESSAGES TO STORE - savedMessages is empty');
        }
        setShowEmailVerification(true);
        console.log('🔐 Express signup: Showing email verification before account setup');
      } else if (signupMode === 'invitation') {
        // Invitation signup - auto-login and redirect
        console.log('✅ Invitation registration successful, proceeding with auto-login');
        
        if (data.access_token) {
          // Call onLogin callback to set authentication state
          if (onLogin) {
            console.log('🔑 Calling onLogin with invitation access token');
            onLogin(data.access_token);
          }
          
          // Show success message and redirect
          setSuccessMessage('Registrering framgångsrik! Du kommer att loggas in automatiskt...');
          setTimeout(() => {
            console.log('🚀 Navigating to secure-chat after invitation registration');
            navigate('/secure-chat');
          }, 1500);
        } else {
          console.error('❌ No access token received from invitation registration');
          setError('Registrering lyckades men inloggning misslyckades. Försök logga in manuellt.');
        }
      } else {
        // Standard signup - show email verification message
        setRegisteredEmail(formData.email);
        setShowEmailVerification(true);
      }

    } catch (error) {
      console.error('Registreringsfel:', error);
      
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Något gick fel vid registreringen. Försök igen.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderModeHeader = () => {
    if (signupMode === 'express') {
      return (
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-primary mb-2 font-inter">
            Utforska dina livsteman
          </h1>
          <p className="text-secondary">
            Identifiera och utforska dina livsteman genom korta, schemabaserade frågor — börja med en snabb utvärdering.
          </p>
        </div>
      );
    } else if (signupMode === 'invitation') {
      return (
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-secondary rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Välkommen till terapin
          </h1>
          <p className="text-gray-600">
            Du har blivit inbjuden av din terapeut - fyll i dina uppgifter nedan
          </p>
        </div>
      );
    } else {
      return (
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-accent rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Skapa konto
          </h1>
          <p className="text-gray-600">
            Välj din roll och fyll i dina uppgifter
          </p>
        </div>
      );
    }
  };

  const renderRoleSelector = () => {
    if (signupMode === 'express' || signupMode === 'invitation') {
      return null; // Role is predetermined
    }

    return (
      <div className="mb-6">
        <label className="block text-sm font-medium text-secondary mb-3">
          Registrera dig som patient/klient
        </label>
        <div className="grid grid-cols-1 gap-4">
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, role: 'patient' }))}
            className={`p-4 border-2 rounded-lg text-center transition-custom ${
              formData.role === 'patient'
                ? 'border-brand-primary bg-brand-primary/10 text-brand-primary'
                : 'border-light hover:border-neutral-400'
            }`}
          >
            <User className="h-8 w-8 mx-auto mb-2" />
            <div className="font-medium">Patient</div>
            <div className="text-sm text-tertiary">Söker hjälp och stöd</div>
          </button>
        </div>
        
        <div className="mt-4 p-3 bg-brand-primary/10 border border-brand-primary/20 rounded-lg">
          <p className="text-sm text-brand-primary">
            <strong>Är du terapeut?</strong> Använd den <a href="/register" className="text-brand-primary hover:underline font-medium">dedicerade terapeutregistreringen</a> istället.
          </p>
        </div>
      </div>
    );
  };

  const renderFormFields = () => {
    return (
      <>
        {/* First name for express mode only - moved to top */}
        {signupMode === 'express' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-secondary mb-2">
              Vad ska vi kalla dig?
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="Vad ska vi kalla dig?"
              required
            />
          </div>
        )}

        {/* Email */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-secondary mb-2">
            E-postadress
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            placeholder="din@email.com"
            required
          />
        </div>

        {/* Personal Information for non-express modes */}
        {(signupMode === 'standard' || signupMode === 'invitation' || signupMode === 'therapist') && (
          <>
                        <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Förnamn
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Efternamn
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Telefonnummer (valfritt)
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="+46 70 123 45 67"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                Födelsedatum (valfritt)
              </label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          </>
        )}

        {/* Password fields */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-secondary mb-2">
            Lösenord
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-3 py-2 pr-10 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="Minst 8 tecken"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-tertiary" />
              ) : (
                <Eye className="h-5 w-5 text-tertiary" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Minst 8 tecken, en versal bokstav (A–Z) och en siffra (0–9)
          </p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-secondary mb-2">
            Bekräfta lösenord
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="w-full px-3 py-2 pr-10 border border-light rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
              placeholder="Upprepa lösenordet"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5 text-tertiary" />
              ) : (
                <Eye className="h-5 w-5 text-tertiary" />
              )}
            </button>
          </div>
        </div>
      </>
    );
  };

  // Account Setup Process handlers
  const handleAccountSetupComplete = (receivedAuthToken, setupDetails) => {
    console.log('✅ Account setup complete:', setupDetails);
    console.log('🔍 DEBUG: Received authToken from AccountSetup:', receivedAuthToken ? `${receivedAuthToken.substring(0, 20)}...` : 'NULL');
    
    // Use the token from localStorage if AccountSetup doesn't provide one
    const finalToken = receivedAuthToken || localStorage.getItem('therapy_auth_token') || authToken;
    console.log('🔍 DEBUG: Final token to use:', finalToken ? `${finalToken.substring(0, 20)}...` : 'NULL');
    
    // Call parent login handler now that everything is complete
    if (onLogin && finalToken) {
      console.log('🔑 Calling onLogin with token:', finalToken.substring(0, 20) + '...');
      onLogin(finalToken);
    } else {
      console.error('❌ Cannot call onLogin: onLogin exists?', !!onLogin, 'finalToken exists?', !!finalToken);
    }
    
    // Navigate to client dashboard now that setup is complete
    setShowAccountSetup(false);
    console.log('🚀 Navigating to client dashboard after AccountSetup completion...');
    navigate('/client');
  };

  const handleAccountSetupError = (error) => {
    console.error('❌ Account setup failed:', error);
    setError('Kontokonfigurationen misslyckades. Försök igen eller kontakta support.');
    setShowAccountSetup(false);
  };

  // Show Account Setup Process for Express signup
  if (showAccountSetup) {
    console.log('🎯 RENDERING AccountSetupProcess - showAccountSetup is TRUE');
    console.log('🎯 RENDERING - Props for AccountSetupProcess:', {
      userEmail: registeredEmail,
      hasUserPassword: !!registrationPassword,
      hasAuthToken: !!authToken,
      messageCount: savedMessages.length,
      messagePreview: savedMessages.slice(0, 2).map(msg => ({ 
        type: msg.type || msg.role, 
        content: msg.content?.substring(0, 50) + '...' 
      }))
    });
    
    // CRITICAL DEBUG: Check current state of messages
    console.log('🔍 CRITICAL DEBUG - Current message sources:');
    console.log('  savedMessages (state):', savedMessages.length);
    console.log('  pendingAnamnesisMessages (storage):', JSON.parse(sessionStorage.getItem('pendingAnamnesisMessages') || '[]').length);
    console.log('  instantChatMessages (storage):', JSON.parse(sessionStorage.getItem('instantChatMessages') || '[]').length);
    
    return (
      <AccountSetupProcess
        userEmail={registeredEmail}
        userPassword={registrationPassword}
        authToken={authToken}
        anonymousMessages={savedMessages}
        onComplete={handleAccountSetupComplete}
        onError={handleAccountSetupError}
      />
    );
  }

  // Show email verification form if registration was successful
  if (showEmailVerification) {
    console.log('🎯 RENDERING EmailVerificationForm - showEmailVerification is TRUE');
    console.log('🎯 RENDERING - Email for verification:', registeredEmail);
    return (
      <EmailVerificationForm
        email={registeredEmail}
        onVerificationSuccess={async (verifiedEmail) => {
          // Track email verification success
          trackEvent('Email Verification Success', {
            location: 'express-signup',
            signup_mode: signupMode,
            email: verifiedEmail,
            has_anonymous_messages: savedMessages.length > 0,
            message_count: savedMessages.length
          });

          // Email verification successful for express signup - show AccountSetup
          console.log('✅ Email verified for express signup user:', verifiedEmail);
          console.log('� Proceeding to AccountSetup process...');
          
          // Update credentials for AccountSetup
          setRegisteredEmail(verifiedEmail);
          setRegistrationPassword(formData.password); // Preserve password for AccountSetup
          
          // Try auto-login after verification
          try {
            console.log('🔑 Attempting auto-login with token endpoint...');
            
            // Use form data for OAuth2PasswordRequestForm
            const loginFormData = new FormData();
            loginFormData.append('username', verifiedEmail);
            loginFormData.append('password', formData.password); // Use fresh password
            
            const loginResponse = await fetch(`${API_BASE}/auth/token`, {
              method: 'POST',
              body: loginFormData
            });
            
            if (!loginResponse.ok) {
              const errorData = await loginResponse.text();
              console.error('❌ Login response error:', errorData);
              throw new Error(`Login failed with status: ${loginResponse.status}`);
            }
            
            const loginData = await loginResponse.json();
            const token = loginData.access_token;
            
            console.log('🔍 DEBUG: Email verification auto-login response:', loginData);
            console.log('🔍 DEBUG: Extracted token:', token);
            
            if (!token) {
              throw new Error('No access token received from login');
            }
            
            // Store token for authenticated requests
            localStorage.setItem('therapy_auth_token', token);
            console.log('🔍 DEBUG: Setting authToken state to:', token);
            setAuthToken(token); // Store token for AccountSetup
            console.log('✅ Auto-login successful after verification');
            
            // DON'T call onLogin here - wait for AccountSetup completion
            // if (onLogin) {
            //   onLogin(token);
            // }
            
            // Retrieve pending anonymous messages before showing AccountSetup
            const pendingMessages = JSON.parse(sessionStorage.getItem('pendingAnamnesisMessages') || '[]');
            console.log('🔍 RETRIEVING PENDING MESSAGES:', {
              found: pendingMessages.length,
              storageKey: 'pendingAnamnesisMessages',
              preview: pendingMessages.slice(0, 2).map(msg => ({
                type: msg.type || msg.role,
                content: msg.content?.substring(0, 50) + '...'
              }))
            });
            
            if (pendingMessages.length > 0) {
              console.log(`🔄 Retrieved ${pendingMessages.length} pending anamnesis messages for AccountSetup`);
              setSavedMessages(pendingMessages);
              console.log('🔄 UPDATED savedMessages state with pending messages');
            } else {
              console.log('⚠️ No pending anamnesis messages found in sessionStorage');
              // Check if there are any messages in other storage locations
              const instantChat = JSON.parse(sessionStorage.getItem('instantChatMessages') || '[]');
              const anonymousLocal = JSON.parse(localStorage.getItem('anonymousMessages') || '[]');
              console.log('🔍 FALLBACK CHECK:', {
                instantChatMessages: instantChat.length,
                anonymousMessages: anonymousLocal.length
              });
            }
            
            // Use setTimeout to ensure state updates are processed before showing AccountSetup
            setTimeout(() => {
              // Hide email verification and show account setup with the fresh token
              setShowEmailVerification(false);
              setShowAccountSetup(true);
              
              // CLEAR any previous conversion flag to ensure AccountSetup processes messages
              sessionStorage.removeItem('messagesAlreadyConverted');
              console.log('🧹 Cleared messagesAlreadyConverted flag to ensure AccountSetup processes messages');
            }, 100);
            
            // DEBUG: Check state after short delay
            setTimeout(() => {
              console.log('🔍 DELAYED STATE CHECK:');
              console.log('   showEmailVerification:', showEmailVerification);
              console.log('   showAccountSetup:', showAccountSetup);
              console.log('   Current URL:', window.location.href);
            }, 100);
            
            console.log('🎯 State after verification success:');
            console.log('   showEmailVerification:', false);
            console.log('   showAccountSetup:', true);
            console.log('   fresh token:', token ? `${token.substring(0, 20)}...` : 'null');
            console.log('   authToken state:', authToken ? `${authToken.substring(0, 20)}...` : 'null');
            console.log('   registeredEmail:', verifiedEmail);
            
          } catch (loginError) {
            console.error('❌ Auto-login failed after verification:', loginError);
            setError('Inloggning misslyckades efter verifiering. Försök logga in manuellt.');
            navigate('/login');
            return;
          }
        }}
        onBackToLogin={() => navigate('/login')}
      />
    );
  }

  // DEBUG: Log which render path we're taking
  console.log('🎯 FINAL RENDER - Taking default form path:');
  console.log('   showAccountSetup:', showAccountSetup);
  console.log('   showEmailVerification:', showEmailVerification);
  console.log('   Current timestamp:', new Date().toISOString());
  
  // AGGRESSIVE DEBUG - if showAccountSetup is true but we're not rendering it, something is wrong
  if (showAccountSetup) {
    console.error('🚨 CRITICAL: showAccountSetup is TRUE but we are in default render path!');
    alert('🚨 CRITICAL: showAccountSetup is TRUE but we are in default render path!');
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-surface-primary to-surface-secondary font-sans">
      <div className="w-full max-w-md">
        {/* Dynamic Header based on mode */}
        {renderModeHeader()}

        {/* Role Selector for standard signup */}
        {renderRoleSelector()}

       
        {/* Registration Form */}
        <div className="bg-primary p-8 rounded-2xl shadow-custom-lg border border-light">
          {error && (
            <div className="bg-error-50 border border-error-500/20 rounded-lg p-4 mb-4">
              <p className="text-error-700 text-sm">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="bg-success-50 border border-success-500/20 rounded-lg p-4 mb-4">
              <p className="text-success-700 text-sm">{successMessage}</p>
              {showManualLogin && (
                <button
                  onClick={() => navigate('/login')}
                  className="mt-3 w-full bg-success-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-success-700 transition-custom flex items-center justify-center space-x-2"
                >
                  <span>Gå till inloggning</span>
                </button>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: showManualLogin ? 'none' : 'block' }}>
            {renderFormFields()}

            {/* Terms and conditions acceptance */}
            <div className="mb-4 p-3 bg-surface-secondary border border-light rounded-lg">
              <p className="text-sm text-secondary text-center">
                Genom att skapa konto godkänner jag{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary hover:text-brand-primary-dark font-medium underline"
                >
                  användarvillkoren
                </a>
                .
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-brand-primary text-white py-3 px-4 rounded-lg font-semibold hover:bg-brand-primary-dark transition-custom duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Skapar konto...</span>
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  <span>
                    {signupMode === 'express' ? 'Skapa konto' : 
                     signupMode === 'invitation' ? 'Slutför registrering' :
                     'Skapa konto'}
                  </span>
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Eller</span>
              </div>
            </div>

            {/* Google Sign-In Button */}
            <div className="flex justify-center mb-6">
              <GoogleLogin
                onSuccess={handleGoogleSignup}
                onError={() => {
                  console.error('❌ Google signup failed');
                  setError('Google-registrering misslyckades');
                }}
                text="signup_with"
                shape="rectangular"
                theme="outline"
                size="large"
              />
            </div>

            
          </form>
        </div>

        {/* Removed 'Vad händer sedan' benefits block as requested - no additional section for express signup */}

        {/* Privacy note */}
        <div className="mt-4 text-center">
          <p className="text-xs text-tertiary flex items-center justify-center space-x-1">
            <Shield className="w-4 h-4" />
            <span>100% säkert och konfidentiellt enligt GDPR</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ExpressSignup;
